import React, { createContext, useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { getHostKind, getOrigins } from '../utils/host';
import { getStoredItem, removeStoredItem, setStoredItem } from '../utils/storage';

export const AuthContext = createContext();

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const REFRESH_EXPIRES_AT_KEY = 'refresh_expires_at';
const SESSION_WARNING_SECONDS_KEY = 'session_expiry_warning_seconds';
const SESSION_EXPIRED_KEY = 'finbase.auth_expired';
const HIDDEN_SESSION_CHECK_INTERVAL_MS = 10000;
const VISIBLE_SESSION_CHECK_INTERVAL_MS = 1000;
const SESSION_WARNING_BUFFER_SECONDS = 1;
const SESSION_REFRESH_SUPPRESSION_MS = 2000;

const formatRemaining = (seconds) => {
  const safeSeconds = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const getRemainingSeconds = (expiresAt) => {
  if (!expiresAt) return null;
  const expiresMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresMs)) return null;
  return Math.ceil((expiresMs - Date.now()) / 1000);
};

const clearSessionStorage = () => {
  removeStoredItem(ACCESS_TOKEN_KEY);
  removeStoredItem(REFRESH_TOKEN_KEY);
  removeStoredItem(REFRESH_EXPIRES_AT_KEY);
  removeStoredItem(SESSION_WARNING_SECONDS_KEY);
};

const persistSessionStorage = (accessToken, refreshToken, sessionMetadata = {}) => {
  if (getHostKind() === 'id') return;
  if (accessToken) setStoredItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) setStoredItem(REFRESH_TOKEN_KEY, refreshToken);
  if (sessionMetadata.refresh_expires_at) {
    setStoredItem(REFRESH_EXPIRES_AT_KEY, sessionMetadata.refresh_expires_at);
  }
  if (sessionMetadata.session_expiry_warning_seconds) {
    setStoredItem(SESSION_WARNING_SECONDS_KEY, String(sessionMetadata.session_expiry_warning_seconds));
  }
  window.dispatchEvent(new Event('finbase.auth_tokens_updated'));
};

const buildLoggedOutRedirectUrl = () => {
  const { idOrigin } = getOrigins();
  const redirectUrl = new URL(`${idOrigin}/login`);
  redirectUrl.searchParams.set('return_to', getHostKind() === 'ops' ? 'ops' : 'account');
  redirectUrl.searchParams.set('logged_out', '1');
  return redirectUrl.toString();
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getStoredItem(ACCESS_TOKEN_KEY) || null);
  const [refreshToken, setRefreshToken] = useState(getStoredItem(REFRESH_TOKEN_KEY) || null);
  const [refreshExpiresAt, setRefreshExpiresAt] = useState(getStoredItem(REFRESH_EXPIRES_AT_KEY) || null);
  const [sessionWarningSeconds, setSessionWarningSeconds] = useState(
    Number(getStoredItem(SESSION_WARNING_SECONDS_KEY)) || 120
  );
  const [sessionWarningVisible, setSessionWarningVisible] = useState(false);
  const [sessionRemainingSeconds, setSessionRemainingSeconds] = useState(null);
  const [sessionRefreshLoading, setSessionRefreshLoading] = useState(false);
  const [sessionRefreshError, setSessionRefreshError] = useState('');
  const expiryLogoutInProgress = useRef(false);
  const sessionRefreshInProgress = useRef(false);
  const sessionRefreshGraceUntil = useRef(0);
  const sessionWarningSuppressedUntil = useRef(0);

  useEffect(() => {
    if (getHostKind() === 'id') return;
    if (token) setStoredItem(ACCESS_TOKEN_KEY, token);
    if (refreshToken) setStoredItem(REFRESH_TOKEN_KEY, refreshToken);
    if (refreshExpiresAt) setStoredItem(REFRESH_EXPIRES_AT_KEY, refreshExpiresAt);
    if (sessionWarningSeconds) setStoredItem(SESSION_WARNING_SECONDS_KEY, String(sessionWarningSeconds));
  }, [token, refreshToken, refreshExpiresAt, sessionWarningSeconds]);

  useEffect(() => {
    const syncTokensFromStorage = () => {
      setToken(getStoredItem(ACCESS_TOKEN_KEY) || null);
      setRefreshToken(getStoredItem(REFRESH_TOKEN_KEY) || null);
      setRefreshExpiresAt(getStoredItem(REFRESH_EXPIRES_AT_KEY) || null);
      setSessionWarningSeconds(Number(getStoredItem(SESSION_WARNING_SECONDS_KEY)) || 120);
    };
    window.addEventListener('finbase.auth_tokens_updated', syncTokensFromStorage);
    return () => window.removeEventListener('finbase.auth_tokens_updated', syncTokensFromStorage);
  }, []);

  const applySessionMetadata = (data = {}) => {
    if (data.refresh_expires_at) setRefreshExpiresAt(data.refresh_expires_at);
    if (data.session_expiry_warning_seconds) {
      setSessionWarningSeconds(Number(data.session_expiry_warning_seconds) || 120);
    }
  };

  const login = async (email, password, returnTo) => {
    const response = await api.post('/acct-mgmt/v1/login', { email, password, return_to: returnTo });
    if (response.data.access_token) setToken(response.data.access_token);
    if (response.data.refresh_token) setRefreshToken(response.data.refresh_token);
    applySessionMetadata(response.data);
    persistSessionStorage(response.data.access_token, response.data.refresh_token, response.data);
    return response.data;
  };

  const signupComplete = (accessToken, refreshToken, sessionMetadata = {}) => {
    setToken(accessToken);
    setRefreshToken(refreshToken);
    applySessionMetadata(sessionMetadata);
    persistSessionStorage(accessToken, refreshToken, sessionMetadata);
  };

  const logout = async () => {
    let redirectUrl = null;
    const currentRefreshToken = getStoredItem(REFRESH_TOKEN_KEY) || refreshToken;
    if (currentRefreshToken) {
      try {
        const response = await api.post('/acct-mgmt/v1/auth/logout', { refresh_token: currentRefreshToken });
        redirectUrl = response.data?.redirect_url || null;
      } catch (err) {
        redirectUrl = null;
      }
    }
    setToken(null);
    setRefreshToken(null);
    setRefreshExpiresAt(null);
    clearSessionStorage();
    const finalRedirectUrl = redirectUrl || buildLoggedOutRedirectUrl();
    window.location.replace(finalRedirectUrl);
    return { redirect_url: finalRedirectUrl };
  };

  const refresh = async ({ logoutOnFailure = true } = {}) => {
    try {
      const currentRefreshToken = getStoredItem(REFRESH_TOKEN_KEY) || refreshToken;
      if (!currentRefreshToken) return false;
      const response = await api.post(
        '/acct-mgmt/v1/auth/refresh',
        { refresh_token: currentRefreshToken },
        { skipAuthRedirect: !logoutOnFailure }
      );
      setToken(response.data.access_token);
      if (response.data.refresh_token) setRefreshToken(response.data.refresh_token);
      applySessionMetadata(response.data);
      persistSessionStorage(response.data.access_token, response.data.refresh_token, response.data);
      return response.data;
    } catch (err) {
      if (logoutOnFailure) logout();
      return false;
    }
  };

  useEffect(() => {
    if (getHostKind() === 'id' || !refreshToken || !refreshExpiresAt) {
      setSessionWarningVisible(false);
      return undefined;
    }
    let interval = null;
    const checkSessionExpiry = () => {
      const latestRefreshToken = getStoredItem(REFRESH_TOKEN_KEY);
      const latestExpiresAt = getStoredItem(REFRESH_EXPIRES_AT_KEY);
      const latestWarningSeconds = Number(getStoredItem(SESSION_WARNING_SECONDS_KEY)) || sessionWarningSeconds || 120;
      if (!latestRefreshToken || !latestExpiresAt) {
        setSessionWarningVisible(false);
        return;
      }
      const remaining = getRemainingSeconds(latestExpiresAt);
      if (remaining === null) {
        setSessionWarningVisible(false);
        return;
      }
      setSessionRemainingSeconds(remaining);
      if (Date.now() < sessionWarningSuppressedUntil.current) {
        setSessionWarningVisible(false);
        setSessionRefreshError('');
        return;
      }
      if (remaining <= 0) {
        if (sessionRefreshInProgress.current || Date.now() < sessionRefreshGraceUntil.current) {
          setSessionWarningVisible(true);
          setSessionRemainingSeconds(0);
          return;
        }
        const finalRemaining = getRemainingSeconds(getStoredItem(REFRESH_EXPIRES_AT_KEY));
        if (finalRemaining !== null && finalRemaining > 0) {
          setSessionWarningVisible(finalRemaining <= latestWarningSeconds - SESSION_WARNING_BUFFER_SECONDS);
          setSessionRemainingSeconds(finalRemaining);
          return;
        }
        if (!expiryLogoutInProgress.current) {
          expiryLogoutInProgress.current = true;
          setStoredItem(SESSION_EXPIRED_KEY, '1');
          logout();
        }
        return;
      }
      const shouldShowWarning = remaining <= latestWarningSeconds - SESSION_WARNING_BUFFER_SECONDS;
      setSessionWarningVisible(shouldShowWarning);
      if (!shouldShowWarning) {
        setSessionRefreshError('');
      }
      const desiredInterval = shouldShowWarning ? VISIBLE_SESSION_CHECK_INTERVAL_MS : HIDDEN_SESSION_CHECK_INTERVAL_MS;
      if (interval?._delay !== desiredInterval) {
        window.clearInterval(interval?.id);
        interval = {
          id: window.setInterval(checkSessionExpiry, desiredInterval),
          _delay: desiredInterval,
        };
      }
    };
    checkSessionExpiry();
    if (!interval) {
      interval = {
        id: window.setInterval(checkSessionExpiry, HIDDEN_SESSION_CHECK_INTERVAL_MS),
        _delay: HIDDEN_SESSION_CHECK_INTERVAL_MS,
      };
    }
    return () => window.clearInterval(interval?.id);
  }, [refreshToken, refreshExpiresAt, sessionWarningSeconds]);

  const handleKeepLoggedIn = async () => {
    if (sessionRefreshInProgress.current) return;
    sessionRefreshInProgress.current = true;
    sessionRefreshGraceUntil.current = Date.now() + 15000;
    setSessionRefreshLoading(true);
    setSessionRefreshError('');
    try {
      const refreshedSession = await refresh({ logoutOnFailure: false });
      if (refreshedSession) {
        expiryLogoutInProgress.current = false;
        sessionRefreshGraceUntil.current = 0;
        sessionWarningSuppressedUntil.current = Date.now() + SESSION_REFRESH_SUPPRESSION_MS;
        const remaining = getRemainingSeconds(refreshedSession.refresh_expires_at);
        if (refreshedSession.refresh_expires_at) {
          setRefreshExpiresAt(refreshedSession.refresh_expires_at);
        }
        if (refreshedSession.session_expiry_warning_seconds) {
          setSessionWarningSeconds(Number(refreshedSession.session_expiry_warning_seconds) || 120);
        }
        if (remaining !== null) {
          setSessionRemainingSeconds(remaining);
        }
        setSessionWarningVisible(false);
        setSessionRefreshError('');
      } else if (!expiryLogoutInProgress.current) {
        setSessionWarningVisible(true);
        setSessionRefreshError('Unable to extend your session. Please try again.');
      }
    } finally {
      sessionRefreshInProgress.current = false;
      setSessionRefreshLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ token, refreshToken, login, signupComplete, logout, refresh }}>
      {children}
      {sessionWarningVisible && getHostKind() !== 'id' && (
        <>
          <div className="modal-backdrop show" />
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Are you still there?</h5>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    You will be logged out due to inactivity in{' '}
                    <strong>{formatRemaining(sessionRemainingSeconds || 0)}</strong>.
                  </p>
                  {sessionRefreshError && (
                    <div className="alert alert-danger mt-3 mb-0" role="alert">
                      {sessionRefreshError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={logout}>
                    Log Out
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleKeepLoggedIn}
                    disabled={sessionRefreshLoading}
                  >
                    {sessionRefreshLoading ? 'Extending...' : 'Keep Me Logged In'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </AuthContext.Provider>
  );
};
